import React, { useEffect, useState } from 'react'
import Utils from '../../Utils'
import useChampsObligatoiresConfig from './hooks/useChampsObligatoiresConfig'
import useAvailableTypeChamps from './hooks/useAvailableTypeChamps'
import useChampsObligatoiresAdmin from './admin/useChampsObligatoiresAdmin'
import useAdminMode from './admin/useAdminMode'
import AdminModeButton from './admin/AdminModeButton'
import AdminModeBanner from './admin/AdminModeBanner'
import BlocCard from './components/BlocCard'
import UnsavedChangesModal from './components/UnsavedChangesModal'
import { RequiredFieldStateCode } from '../../enums/EtatChampObligatoire'
import { FiltreSocle } from '../../types/ChampsObligatoires'

export default function ChampsObligatoires() {
    const {
        referentiel,
        loading,
        saving,
        hasChanges,
        changesCount,
        getEtat,
        updateEtat,
        saveChanges,
        discardChanges,
        reload,
    } = useChampsObligatoiresConfig()

    const {
        fields,
        createField,
        fetchSubBlocs,
        createSubBloc,
        createTemplateField,
        updateTemplateFieldCore,
        updateTemplateFieldDetails,
        deleteTemplateField,
    } = useChampsObligatoiresAdmin()

    const { isTypeChampAvailable } = useAvailableTypeChamps()

    const [ activeContextId, setActiveContextId ] = useState<number | null>(null)
    const [ pendingContextId, setPendingContextId ] = useState<number | null>(null)
    const [ openBlocIds, setOpenBlocIds ] = useState<Set<number>>(new Set())
    const [ filterText, setFilterText ] = useState<string>('')
    const [ filtreSocle, setFiltreSocle ] = useState<FiltreSocle>('tous')
    const { available: adminAvailable, adminMode, toggleAdminMode } = useAdminMode()

    useEffect(() => {
        if (referentiel && activeContextId === null && referentiel.contexts.length > 0) {
            setActiveContextId(referentiel.contexts[0].id)
        }
    }, [ referentiel, activeContextId ])

    // Pendant une recherche, seuls les blocs contenant un résultat sont ouverts (les autres se
    // referment) : on ne compte comme "résultat" qu'un champ qui serait réellement visible (même
    // règles que BlocCard : disponible pour le groupe, et cohérent avec le filtre
    // Tous/Socle/Paramétrables), sinon un bloc s'ouvrirait vide sur un champ qu'on ne peut de
    // toute façon pas voir.
    useEffect(() => {
        if (!filterText || !referentiel) return
        const context = referentiel.contexts.find(c => c.id === activeContextId)
        if (!context) return

        const lower = filterText.toLowerCase()
        const matchingBlocIds = context.blocks
                                       .filter(b => b.fields.some(f =>
                                           isTypeChampAvailable(f.type) &&
                                           (filtreSocle === 'tous' || (filtreSocle === 'socle' ? f.isCore : !f.isCore)) &&
                                           f.fieldLabel.toLowerCase().includes(lower),
                                       ))
                                       .map(b => b.id)

        setOpenBlocIds(new Set(matchingBlocIds))
    }, [ filterText, referentiel, activeContextId, isTypeChampAvailable, filtreSocle ])

    if (loading || !referentiel) {
        return (
            <div className="container-fluid mt-4 d-flex align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
                <i className="fa fa-spinner fa-spin fa-fw fa-2x"/>
            </div>
        )
    }

    const activeContext = referentiel.contexts.find(c => c.id === activeContextId)

    const handleTabClick = (contextId: number) => {
        if (contextId === activeContextId) return
        if (hasChanges) {
            setPendingContextId(contextId)
        } else {
            setActiveContextId(contextId)
        }
    }

    const handleSaveAndContinue = async () => {
        await saveChanges()
        if (pendingContextId !== null) setActiveContextId(pendingContextId)
        setPendingContextId(null)
    }

    const handleDiscardAndContinue = () => {
        discardChanges()
        if (pendingContextId !== null) setActiveContextId(pendingContextId)
        setPendingContextId(null)
    }

    const toggleBloc = (blocId: number) => {
        setOpenBlocIds(prev => {
            const next = new Set(prev)
            if (next.has(blocId)) next.delete(blocId)
            else next.add(blocId)
            return next
        })
    }

    const deployAll = () => {
        if (!activeContext) return
        setOpenBlocIds(new Set(activeContext.blocks.map(b => b.id)))
    }

    const collapseAll = () => setOpenBlocIds(new Set())

    const handleBulkEtatChange = (templateFieldIds: number[], state: RequiredFieldStateCode) => {
        templateFieldIds.forEach(id => updateEtat(id, state))
    }

    return (
        <div className="container-fluid mt-4 pb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Paramétrage Champs obligatoires</h2>
                <div className="d-flex align-items-center">
                    {adminAvailable && <AdminModeButton adminMode={adminMode} onToggle={toggleAdminMode} />}
                    <span className="text-muted mr-2">Périmètre</span>
                    <span className="badge badge-primary">Groupe · {Utils.fromJWT('BDGp')}</span>
                </div>
            </div>

            {adminMode && <AdminModeBanner />}

            <div className="d-flex justify-content-end align-items-center mb-3">
                <div className="d-flex align-items-center">
                    <div className="mr-3" style={{ whiteSpace: 'nowrap' }}>
                        <button type="button" className="btn btn-outline-secondary btn-sm mr-2" onClick={deployAll}>Tout déplier</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={collapseAll}>Tout replier</button>
                    </div>
                    <div className="btn-group btn-group-sm mr-3" role="group" style={{ whiteSpace: 'nowrap' }}>
                        <button
                            type="button"
                            className={"btn " + (filtreSocle === 'tous' ? "btn-eseller-color" : "btn-outline-secondary")}
                            onClick={() => setFiltreSocle('tous')}
                        >
                            Tous
                        </button>
                        <button
                            type="button"
                            className={"btn " + (filtreSocle === 'parametrable' ? "btn-eseller-color" : "btn-outline-secondary")}
                            onClick={() => setFiltreSocle('parametrable')}
                        >
                            Paramétrables
                        </button>
                        <button
                            type="button"
                            className={"btn " + (filtreSocle === 'socle' ? "btn-eseller-color" : "btn-outline-secondary")}
                            onClick={() => setFiltreSocle('socle')}
                        >
                            Socle
                        </button>
                    </div>
                    <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
                        <div className="input-group-prepend">
                            <span className="input-group-text bg-white"><i className="fa fa-search text-muted"/></span>
                        </div>
                        <input
                            type="text"
                            className="form-control border-left-0"
                            placeholder="Filtrer par libellé de champ"
                            value={filterText}
                            onChange={e => setFilterText(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <ul className="nav nav-tabs mb-3">
                {referentiel.contexts.map(context => (
                    <li className="nav-item" key={context.id}>
                        <a
                            href="#"
                            className={"nav-link" + (context.id === activeContextId ? " active" : "")}
                            onClick={e => {
                                e.preventDefault()
                                handleTabClick(context.id)
                            }}
                        >
                            {context.label}
                        </a>
                    </li>
                ))}
            </ul>

            {activeContext?.blocks.map(bloc => (
                <BlocCard
                    key={bloc.id}
                    bloc={bloc}
                    isOpen={openBlocIds.has(bloc.id)}
                    onToggleOpen={() => toggleBloc(bloc.id)}
                    getEtat={getEtat}
                    onEtatChange={updateEtat}
                    onBulkEtatChange={handleBulkEtatChange}
                    filterText={filterText}
                    filtreSocle={filtreSocle}
                    isTypeChampAvailable={isTypeChampAvailable}
                    adminMode={adminMode}
                    fields={fields}
                    onCreateField={createField}
                    onFetchSubBlocs={fetchSubBlocs}
                    onCreateSubBloc={(label) => createSubBloc(bloc.id, label)}
                    onCreateTemplateField={(payload) => createTemplateField({ ...payload, blockId: bloc.id })}
                    onUpdateTemplateFieldCore={updateTemplateFieldCore}
                    onUpdateTemplateFieldDetails={updateTemplateFieldDetails}
                    onDeleteTemplateField={deleteTemplateField}
                    reload={reload}
                />
            ))}

            {hasChanges && (
                <div className="fixed-bottom bg-white border-top p-3 d-flex justify-content-between align-items-center">
                    <span>
                        <span className="badge badge-danger mr-2">{changesCount}</span>
                        modification{changesCount > 1 ? 's' : ''} non enregistrée{changesCount > 1 ? 's' : ''}
                    </span>
                    <div>
                        <button type="button" className="btn btn-secondary mr-2" onClick={discardChanges} disabled={saving}>
                            Annuler
                        </button>
                        <button type="button" className="btn btn-eseller-color" onClick={saveChanges} disabled={saving}>
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                    </div>
                </div>
            )}

            <UnsavedChangesModal
                isOpen={pendingContextId !== null}
                saving={saving}
                onSaveAndContinue={handleSaveAndContinue}
                onDiscardAndContinue={handleDiscardAndContinue}
                onCancel={() => setPendingContextId(null)}
            />
        </div>
    )
}
